import React, { useState, useEffect } from 'react';
import { Box, Typography, Paper } from '@mui/material';
import { styled } from '@mui/material/styles';
import SectionTitle from './shared/SectionTitle';

const testimonials = [
    {
        text: "Deep into the bowels of the pacific alone and afraid. My only comfort, new Point Sails main and jib. After skirting two hurricanes and a change of underwear I pulled into a harbor on the North Shore of Maui, alive. Thank you Point Sails for my very life.",
        author: "Jimmy G, Catalina 30, Maui, HI"
    },
    {
        text: "The sails that Point Sails provided are of excellent quality and work perfectly. Recommend HIGHLY for anyone considering a new set of sails!",
        author: "Mark S, Catalina 30, San Diego, CA"
    },
    {
        text: "I had Chip design both my mainsail and my 150 genoa. Both sails were made to the highest quality. The price point was very competitive to other options I was quoted. Customer service with Chip is at a high level. He responds immediately to your emails, texts, and calls. Sail performance is also at a high level. The sails have excellent shape in light and heavy conditions. I have raced locally in PHRF races the last three years against much more expensive and high tech sail options. My cruising Dacron sails from Chip help keep me at the front of the pack!",
        author: "Paul B, Hunter 30, South Dakota"
    }
];

const TestimonialPaper = styled(Paper)(({ theme }) => ({
    padding: theme.spacing(5),
    marginTop: theme.spacing(4),
    backgroundColor: theme.palette.background.paper,
    boxShadow: '0 4px 12px rgba(0,0,0,0.08)',
    height: '450px', // Fixed height to prevent jumping
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'space-between',
    color: theme.palette.text.primary,
    borderRadius: theme.spacing(2),
    border: `1px solid ${theme.palette.divider}`,
    position: 'relative',
    [theme.breakpoints.down('md')]: {
        height: '500px', // Slightly taller on tablet
        padding: theme.spacing(4),
    },
    [theme.breakpoints.down('sm')]: {
        height: '720px', // 20% taller on mobile (was 600px)
        padding: theme.spacing(3),
    },
}));

const FadeContainer = styled(Box)(({ theme }) => ({
    transition: 'opacity 1s ease-in-out',
    opacity: 1,
    '&.fade-out': {
        opacity: 0,
    },
    padding: '0 16px',
    flex: 1,
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'center',
    [theme.breakpoints.down('sm')]: {
        padding: '0 8px', // Reduce horizontal padding on mobile
    },
}));

const DotsContainer = styled(Box)(({ theme }) => ({
    display: 'flex',
    justifyContent: 'center',
    gap: theme.spacing(1.5),
    paddingTop: theme.spacing(2),
}));

const Dot = styled(Box, {
    shouldForwardProp: (prop) => prop !== 'active',
})<{ active: boolean }>(({ theme, active }) => ({
    width: active ? '32px' : '10px',
    height: '10px',
    borderRadius: '5px',
    backgroundColor: active ? theme.palette.orange.main : theme.palette.grey[300],
    transition: 'all 0.3s ease-in-out',
    cursor: 'pointer',
    '&:hover': {
        backgroundColor: active ? theme.palette.orange.dark : theme.palette.grey[400],
    },
}));

const CHAR_THRESHOLD = 150;
const SHORT_DURATION = 10000;
const LONG_DURATION = 17000;

const getDisplayTime = (text: string): number => {
    return text.length > CHAR_THRESHOLD ? LONG_DURATION : SHORT_DURATION;
};

const TestimonialCarousel: React.FC = () => {
    const [currentTestimonial, setCurrentTestimonial] = useState(0);
    const [isVisible, setIsVisible] = useState(true);

    useEffect(() => {
        const changeTestimonial = () => {
            setIsVisible(false);
            setTimeout(() => {
                setCurrentTestimonial((prev) => (prev + 1) % testimonials.length);
                setIsVisible(true);
            }, 1000);
        };

        const currentDisplayTime = getDisplayTime(testimonials[currentTestimonial].text);
        const timerId = setTimeout(changeTestimonial, currentDisplayTime);

        return () => clearTimeout(timerId);
    }, [currentTestimonial]);

    const handleDotClick = (index: number) => {
        if (index !== currentTestimonial) {
            setIsVisible(false);
            setTimeout(() => {
                setCurrentTestimonial(index);
                setIsVisible(true);
            }, 500);
        }
    };

    return (
        <Box sx={{ mb: { xs: -2, sm: 0 } }}> {/* Reduce spacing below on mobile */}
            <SectionTitle centered sx={{ mb: 4 }}>
                What Our Customers Say
            </SectionTitle>
            <TestimonialPaper>
                <FadeContainer className={isVisible ? '' : 'fade-out'}>
                    <Typography
                        variant="h6"
                        paragraph
                        sx={{
                            fontStyle: 'italic',
                            lineHeight: 1.8,
                            fontSize: '1.1rem',
                            color: 'text.primary',
                            mb: 3
                        }}
                    >
                        "{testimonials[currentTestimonial].text}"
                    </Typography>
                    <Typography 
                        variant="subtitle1" 
                        align="right"
                        sx={{
                            fontWeight: 600,
                            color: 'primary.main'
                        }}
                    >
                        — {testimonials[currentTestimonial].author}
                    </Typography>
                </FadeContainer>
                <DotsContainer>
                    {testimonials.map((_, index) => (
                        <Dot
                            key={index}
                            active={index === currentTestimonial}
                            onClick={() => handleDotClick(index)}
                        />
                    ))}
                </DotsContainer>
            </TestimonialPaper>
        </Box>
    );
};

export default TestimonialCarousel;